import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { NavLink } from "react-router-dom";
import { useState, useEffect } from "react";
import { createPortal } from "react-dom";

interface NavItemProps {
  icon: React.ElementType;
  label: React.ReactNode;
  path: string; // Path is required for NavLink
  onClick?: () => void; // onClick is optional
  isCollapsed: boolean;
  isActive?: boolean; // isActive can be passed in, but NavLink handles it
  badge?: string;
  iconSize?: number | "inherit" | "small" | "medium" | "large";
  component?: React.ComponentType; // Add this line to support rendering components
  disabled?: boolean; // Add disabled state
  disabledTooltip?: string; // Add custom tooltip for disabled state
}

export function NavItem({
  icon: Icon,
  label,
  path,
  isCollapsed,
  onClick,
  badge,
  iconSize = "small",
  isActive,
  disabled = false,
  disabledTooltip = "Coming soon",
}: NavItemProps) {
  const [showCustomTooltip, setShowCustomTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [triggerRef, setTriggerRef] = useState<HTMLDivElement | null>(null);

  const handleMouseEnter = (e: React.MouseEvent) => {
    if (disabled || isCollapsed) {
      const rect = e.currentTarget.getBoundingClientRect();
      setTooltipPosition({
        x: rect.right + 8,
        y: rect.top + rect.height / 2,
      });
      setShowCustomTooltip(true);
    }
  };

  const handleMouseLeave = () => {
    setShowCustomTooltip(false);
  };
  const linkContent = (
    <>
      <div className="flex items-center gap-3">
        <div
          className={cn(
            "transition-all duration-200",
            disabled && "opacity-50"
          )}
        >
          <Icon
            {...(typeof iconSize === "number"
              ? { size: iconSize }
              : { fontSize: iconSize })}
          />
        </div>
        {!isCollapsed && (
          <span
            className={cn(
              "transition-all duration-200 truncate",
              disabled && "opacity-50"
            )}
          >
            {label}
          </span>
        )}
      </div>
      {badge && !isCollapsed && (
        <span className="px-2 py-0.5 text-xs font-medium bg-blue-500/20 text-blue-300 rounded-full">
          {badge}
        </span>
      )}
      {!isCollapsed && (
        <ChevronRight
          className={cn(
            "w-4 h-4 opacity-0 transition-all duration-200 ease-out",
            "group-hover:opacity-50 group-hover:translate-x-1",
            !isCollapsed && "ml-auto",
            disabled && "opacity-0"
          )}
        />
      )}
    </>
  );

  const link = disabled ? (
    <div
      ref={setTriggerRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className={cn(
        "group flex items-center w-full px-3 text-sm font-medium rounded-lg transition-all duration-200 ease-out cursor-not-allowed",
        isCollapsed ? "justify-center py-3" : "justify-start py-2.5",
        "text-muted-foreground opacity-50"
      )}
    >
      {linkContent}
    </div>
  ) : (
    <NavLink
      to={path}
      onClick={onClick}
      aria-label={typeof label === "string" ? label : undefined}
      onMouseEnter={isCollapsed ? handleMouseEnter : undefined}
      onMouseLeave={isCollapsed ? handleMouseLeave : undefined}
      className={() =>
        cn(
          "group flex items-center justify-between w-full px-3 text-sm font-medium rounded-lg transition-all duration-200 ease-out",
          "hover:bg-accent hover:text-accent-foreground hover:shadow-sm",
          !isCollapsed && "hover:translate-x-1",
          isCollapsed ? "justify-center py-3" : "justify-start py-2.5",
          isActive
            ? "bg-accent text-accent-foreground shadow-sm"
            : "text-muted-foreground"
        )
      }
    >
      {linkContent}
    </NavLink>
  );

  if (isCollapsed) {
    // For all items in collapsed state, use custom tooltip to avoid z-index issues
    return (
      <>
        {link}
        {showCustomTooltip &&
          createPortal(
            <div
              className="fixed z-[99999] px-3 py-1.5 text-xs text-primary-foreground bg-primary rounded-md shadow-lg pointer-events-none"
              style={{
                left: tooltipPosition.x,
                top: tooltipPosition.y - 12,
                transform: "translateY(-50%)",
              }}
            >
              {disabled ? disabledTooltip : label}
            </div>,
            document.body
          )}
      </>
    );
  }

  // For non-collapsed state, show custom tooltip when disabled
  if (disabled) {
    return (
      <>
        {link}
        {showCustomTooltip &&
          createPortal(
            <div
              className="fixed z-[99999] px-3 py-1.5 text-xs text-primary-foreground bg-primary rounded-md shadow-lg pointer-events-none"
              style={{
                left: tooltipPosition.x,
                top: tooltipPosition.y - 12,
                transform: "translateY(-50%)",
              }}
            >
              {disabledTooltip}
            </div>,
            document.body
          )}
      </>
    );
  }

  return link;
}
